import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, RefreshCw, Download, CheckCircle, XCircle, Clock, AlertTriangle,
  ArrowDownRight, ArrowUpLeft, Filter, ChevronDown, ChevronUp,
  Receipt, Ban, Shield, FileText, Undo2, Send, Plus, CreditCard, Wallet, ArrowDown, ArrowUp, Landmark
} from 'lucide-react';
import {
  getAdminTransactions, refundTransaction, cancelTransaction,
  reconcileTransaction, reconcileTransactionBatch, adminSendMoney, adminDebitAccount,
  getPendingHiveFundWires, settleHiveFundWire
} from '../../services/adminDealFlowApi';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/new-components/ui/card';
import { Button } from '../../components/new-components/ui/button';
import { Badge } from '../../components/new-components/ui/badge';
import { Input } from '../../components/new-components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/new-components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/new-components/ui/select';

const formatCurrency = (cents: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((cents || 0) / 100);

const formatDate = (d: string) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const formatDateTime = (d: string) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';

// NOC code descriptions
const NOC_CODES: Record<string, string> = {
  C01: 'Incorrect DFI Account Number',
  C02: 'Incorrect Routing Number',
  C03: 'Incorrect Routing + Account Number',
  C04: 'Incorrect Individual Name',
  C05: 'Incorrect Transaction Code',
  C06: 'Incorrect Account + Transaction Code',
  C07: 'Incorrect Routing, Account + Transaction Code',
  C09: 'Incorrect Individual ID Number',
  C61: 'Misrouted Notification of Change',
  C62: 'Incorrect Trace Number',
  C63: 'Incorrect Company ID',
  C64: 'Incorrect Individual ID (IAT)',
};

// ACH return code descriptions
const RETURN_CODES: Record<string, string> = {
  R01: 'Insufficient Funds',
  R02: 'Account Closed',
  R03: 'No Account / Unable to Locate',
  R04: 'Invalid Account Number',
  R05: 'Unauthorized Debit (Consumer)',
  R06: 'Returned per ODFI Request',
  R07: 'Auth Revoked by Customer',
  R08: 'Payment Stopped',
  R09: 'Uncollected Funds',
  R10: 'Customer Advises Originator Not Authorized',
  R11: 'Customer Entry for Multiple Credits',
  R12: 'Branch Sold to Another DFI',
  R13: 'RDFI Not Qualified',
  R14: 'Representative Payee Deceased',
  R15: 'Beneficiary Deceased',
  R16: 'Account Frozen / Entry Returned',
  R17: 'File Record Edit Criteria',
  R20: 'Non-Transaction Account',
  R21: 'Invalid Company ID',
  R22: 'Invalid Individual ID',
  R23: 'Credit Entry Refused by Receiver',
  R24: 'Duplicate Entry',
  R29: 'Corporate Customer Advises Not Authorized',
};

const statusConfig: Record<string, { color: 'default' | 'secondary' | 'destructive' | 'outline'; icon: React.ReactNode }> = {
  pending: { color: 'outline', icon: <Clock className="h-3.5 w-3.5 text-gray-400" /> },
  processing: { color: 'secondary', icon: <RefreshCw className="h-3.5 w-3.5 text-blue-500" /> },
  settled: { color: 'default', icon: <CheckCircle className="h-3.5 w-3.5 text-green-600" /> },
  failed: { color: 'destructive', icon: <XCircle className="h-3.5 w-3.5 text-red-500" /> },
  returned: { color: 'destructive', icon: <ArrowUpLeft className="h-3.5 w-3.5 text-red-500" /> },
  refunded: { color: 'secondary', icon: <Undo2 className="h-3.5 w-3.5 text-orange-500" /> },
  partially_refunded: { color: 'secondary', icon: <Undo2 className="h-3.5 w-3.5 text-amber-500" /> },
  cancelled: { color: 'outline', icon: <Ban className="h-3.5 w-3.5 text-gray-400" /> },
};

// Unwrap API response: handles both axios-wrapped {data: {success, data, ...}} and direct body {success, data, ...}
const unwrapRes = (res: any) => {
  const body = res?.data?.success !== undefined ? res.data : (res?.success !== undefined ? res : res?.data || res);
  return body;
};

const AdminTransactions: React.FC = () => {
  const [transactions, setTransactions] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 50;

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [typeTab, setTypeTab] = useState('all');
  const [reconcileFilter, setReconcileFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [hasNoc, setHasNoc] = useState(false);
  const [hasReturn, setHasReturn] = useState(false);

  // Detail / Action dialogs
  const [selected, setSelected] = useState<any>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showRefund, setShowRefund] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Pending Hive Fund wires awaiting manual settlement
  const [pendingWires, setPendingWires] = useState<any[]>([]);
  const [settlingWireId, setSettlingWireId] = useState<string | null>(null);

  // Batch selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // New Transfer dialog
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferDirection, setTransferDirection] = useState<'send' | 'debit'>('send');
  const [transferMethod, setTransferMethod] = useState<'ach' | 'usdc'>('ach');
  const [transferName, setTransferName] = useState('');
  const [transferEmail, setTransferEmail] = useState('');
  const [transferAmount, setTransferAmount] = useState('');
  const [transferDesc, setTransferDesc] = useState('');
  const [transferNotes, setTransferNotes] = useState('');
  const [transferRouting, setTransferRouting] = useState('');
  const [transferAccount, setTransferAccount] = useState('');
  const [transferAccountType, setTransferAccountType] = useState<'checking' | 'savings'>('checking');
  const [transferWallet, setTransferWallet] = useState('');
  const [transferChain, setTransferChain] = useState('base');
  const [transferResult, setTransferResult] = useState<string | null>(null);
  const [transferError, setTransferError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = { page, limit, search };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (methodFilter !== 'all') params.method = methodFilter;
      if (reconcileFilter !== 'all') params.reconciled = reconcileFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (hasNoc) params.has_noc = 'true';
      if (hasReturn) params.has_return = 'true';
      if (sourceFilter !== 'all') params.source = sourceFilter;
      if (typeTab !== 'all') params.transaction_type = typeTab;

      const res = await getAdminTransactions(params);
      const body = unwrapRes(res);
      const items = Array.isArray(body?.data) ? body.data : [];
      setTransactions(items);
      setSummary(body?.summary || {});
      setTotal(body?.pagination?.total || items.length);
    } catch (err) {
      console.error('Failed to load transactions', err);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, methodFilter, sourceFilter, typeTab, reconcileFilter, dateFrom, dateTo, hasNoc, hasReturn]);

  useEffect(() => { load(); }, [load]);

  const loadPendingWires = useCallback(async () => {
    try {
      const res = await getPendingHiveFundWires();
      const body = unwrapRes(res);
      setPendingWires(Array.isArray(body?.data) ? body.data : []);
    } catch (err) {
      console.error('Failed to load pending wires', err);
    }
  }, []);

  useEffect(() => { loadPendingWires(); }, [loadPendingWires]);

  // Mark a pending Hive Fund wire as settled
  const handleSettleWire = async (wire: any) => {
    if (settlingWireId) return;
    const confirmed = window.confirm(
      `Mark the ${formatCurrency(wire.amountCents)} wire from ${wire.investorName} as settled?\n\n` +
      `This activates their Hive Fund position and emails a confirmation. Only do this once the funds have landed.`
    );
    if (!confirmed) return;
    try {
      setSettlingWireId(wire.investmentId);
      await settleHiveFundWire(wire.investmentId);
      await Promise.all([loadPendingWires(), load()]);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to settle wire';
      window.alert(`Could not settle wire: ${msg}`);
      console.error('Settle wire failed', err);
    } finally {
      setSettlingWireId(null);
    }
  };

  // Refund handler
  const handleRefund = async () => {
    if (!selected) return;
    try {
      setActionLoading(true);
      const cents = refundAmount ? Math.round(parseFloat(refundAmount) * 100) : undefined;
      await refundTransaction(selected.id, { amount_cents: cents, reason: refundReason || undefined });
      setShowRefund(false);
      setShowDetail(false);
      setRefundAmount('');
      setRefundReason('');
      load();
    } catch (err) {
      console.error('Refund failed', err);
    } finally {
      setActionLoading(false);
    }
  };

  // Cancel handler
  const handleCancel = async (txn: any) => {
    try {
      setActionLoading(true);
      await cancelTransaction(txn.id, { reason: 'Admin cancelled' });
      setShowDetail(false);
      load();
    } catch (err) {
      console.error('Cancel failed', err);
    } finally {
      setActionLoading(false);
    }
  };

  // Reconcile handler
  const handleReconcile = async (txn: any) => {
    try {
      setActionLoading(true);
      await reconcileTransaction(txn.id);
      load();
      if (selected?.id === txn.id) setSelected({ ...selected, reconciled: true });
    } catch (err) {
      console.error('Reconcile failed', err);
    } finally {
      setActionLoading(false);
    }
  };

  // Batch reconcile
  const handleBatchReconcile = async () => {
    if (selectedIds.size === 0) return;
    try {
      setActionLoading(true);
      await reconcileTransactionBatch(Array.from(selectedIds));
      setSelectedIds(new Set());
      load();
    } catch (err) {
      console.error('Batch reconcile failed', err);
    } finally {
      setActionLoading(false);
    }
  };

  // New Transfer handler
  const resetTransferForm = () => {
    setTransferDirection('send');
    setTransferMethod('ach');
    setTransferName('');
    setTransferEmail('');
    setTransferAmount('');
    setTransferDesc('');
    setTransferNotes('');
    setTransferRouting('');
    setTransferAccount('');
    setTransferAccountType('checking');
    setTransferWallet('');
    setTransferChain('base');
    setTransferResult(null);
    setTransferError(null);
  };

  const handleTransferSubmit = async () => {
    setActionLoading(true);
    setTransferError(null);
    setTransferResult(null);
    try {
      const amountCents = Math.round(parseFloat(transferAmount) * 100);
      if (!amountCents || amountCents <= 0) {
        setTransferError('Enter a valid positive amount');
        return;
      }
      let res: any;
      if (transferDirection === 'send') {
        res = await adminSendMoney({
          recipient_name: transferName,
          recipient_email: transferEmail || undefined,
          amount_cents: amountCents,
          description: transferDesc || undefined,
          method: transferMethod,
          notes: transferNotes || undefined,
          ach_routing: transferMethod === 'ach' ? transferRouting : undefined,
          ach_account_number: transferMethod === 'ach' ? transferAccount : undefined,
          ach_account_type: transferMethod === 'ach' ? transferAccountType : undefined,
          wallet_address: transferMethod === 'usdc' ? transferWallet : undefined,
          chain: transferMethod === 'usdc' ? transferChain : undefined,
        });
      } else {
        res = await adminDebitAccount({
          payer_name: transferName,
          payer_email: transferEmail || undefined,
          amount_cents: amountCents,
          description: transferDesc || undefined,
          notes: transferNotes || undefined,
          ach_routing: transferRouting,
          ach_account_number: transferAccount,
          ach_account_type: transferAccountType,
        });
      }
      const body = res?.data || res;
      if (body?.success === false) {
        setTransferError(body.message || 'Transfer failed');
      } else {
        setTransferResult(body?.message || 'Transfer initiated successfully');
        load();
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Transfer failed';
      setTransferError(msg);
    } finally {
      setActionLoading(false);
    }
  };

  const toggleSelected = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === transactions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(transactions.map(t => t.id)));
    }
  };

  // CSV export
  const exportCSV = () => {
    const headers = ['TXN ID', 'Date', 'Source', 'Engagement/Deal', 'User', 'Amount', 'Fees', 'Net', 'Method', 'Bank Account', 'Status', 'NOC Code', 'Return Code', 'Refund Amount', 'Settlement Date', 'Reconciled'];
    const rows = transactions.map(t => [
      t.txn_id, formatDateTime(t.created_at), t.transaction_type || t.source || 'engagement', t.engagement_title || '', t.user_email || '',
      (t.amount_cents / 100).toFixed(2), ((t.fees_cents || 0) / 100).toFixed(2),
      ((t.net_amount_cents || 0) / 100).toFixed(2), t.method, t.bank_account_name || '', t.status,
      t.noc_code || '', t.return_code || '', ((t.refund_amount_cents || 0) / 100).toFixed(2),
      t.settlement_date || '', t.reconciled ? 'Yes' : 'No'
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `transactions_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-5 sm:space-y-6 min-w-0">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 lg:gap-4">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-display font-semibold text-[#242424]">Transactions</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Merchant report & reconciliation — {total} transactions
          </p>
        </div>
        <div className="grid grid-cols-2 sm:flex flex-wrap gap-2 w-full lg:w-auto">
          <Button size="sm" onClick={() => { resetTransferForm(); setShowTransfer(true); }} className="justify-center gap-1 bg-blue-600 hover:bg-blue-700 text-white">
            <Plus className="h-4 w-4" /> New Transfer
          </Button>
          {selectedIds.size > 0 && (
            <Button variant="outline" size="sm" onClick={handleBatchReconcile} disabled={actionLoading} className="justify-center gap-1">
              <Shield className="h-4 w-4" /> Reconcile ({selectedIds.size})
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={exportCSV} className="justify-center gap-1">
            <Download className="h-4 w-4" /> Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={load} className="justify-center gap-1">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Pending Wires — manual settlement queue (only shown when wires await action) */}
      {pendingWires.length > 0 && (
        <Card className="border-amber-300 bg-amber-50/60">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base text-amber-900">
              <Landmark className="h-4 w-4" />
              Pending Wires — Awaiting Settlement
              <Badge variant="outline" className="ml-1 border-amber-400 text-amber-800">{pendingWires.length}</Badge>
            </CardTitle>
            <p className="text-xs text-amber-800/80">
              Mark a wire settled once funds have landed. This activates the investor's Hive Fund position and emails a confirmation.
            </p>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-amber-200">
              {pendingWires.map((w) => (
                <div key={w.investmentId} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="font-medium text-[#242424] truncate">
                      {w.investorName} <span className="font-mono text-sm text-muted-foreground">· {formatCurrency(w.amountCents)}</span>
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {w.investorEmail} · {w.confirmationCode} · created {formatDate(w.createdAt)}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleSettleWire(w)}
                    disabled={settlingWireId === w.investmentId}
                    className="shrink-0 justify-center gap-1 bg-amber-600 hover:bg-amber-700 text-white"
                  >
                    {settlingWireId === w.investmentId
                      ? (<><RefreshCw className="h-3.5 w-3.5 animate-spin" /> Settling…</>)
                      : (<><CheckCircle className="h-3.5 w-3.5" /> Mark Settled</>)}
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Type Tabs */}
      <div className="flex gap-1 border-b border-border overflow-x-auto overscroll-x-contain">
        {[
          { key: 'all', label: 'All Transactions' },
          { key: 'engagement', label: 'Engagements' },
          { key: 'vendor_payment', label: 'Vendor' },
          { key: 'payroll', label: 'Payroll' },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => { setTypeTab(tab.key); setPage(1); }}
            className={`shrink-0 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              typeTab === tab.key
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Card className="border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Gross Volume</p>
            <p className="text-lg font-semibold text-[#242424]">{formatCurrency(summary.gross_volume_cents || summary.total_settled_cents || 0)}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Net Settled</p>
            <p className="text-lg font-semibold text-green-700">{formatCurrency(summary.total_settled_cents || summary.net_settled_cents || 0)}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Pending</p>
            <p className="text-lg font-semibold text-amber-600">{formatCurrency(summary.total_pending_cents || summary.pending_cents || 0)}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Failed / Returns</p>
            <p className="text-lg font-semibold text-red-600">{Number(summary.failed_count || 0) + Number(summary.return_count || 0)}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">NOC Codes</p>
            <p className="text-lg font-semibold text-orange-600">{summary.noc_count || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Unreconciled</p>
            <p className="text-lg font-semibold text-[#242424]">{summary.unreconciled_count || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_auto_auto_auto_auto_auto] gap-3 lg:items-center">
        <div className="relative min-w-0 sm:col-span-2 lg:col-span-1 lg:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search TXN ID, email, engagement..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-full lg:w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="processing">Processing</SelectItem>
            <SelectItem value="settled">Settled</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="returned">Returned</SelectItem>
            <SelectItem value="refunded">Refunded</SelectItem>
            <SelectItem value="partially_refunded">Partial Refund</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Select value={methodFilter} onValueChange={v => { setMethodFilter(v); setPage(1); }}>
          <SelectTrigger className="w-full lg:w-32">
            <SelectValue placeholder="Method" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Methods</SelectItem>
            <SelectItem value="ach">ACH</SelectItem>
            <SelectItem value="usdc">USDC</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sourceFilter} onValueChange={v => { setSourceFilter(v); setPage(1); }}>
          <SelectTrigger className="w-full lg:w-36">
            <SelectValue placeholder="Source" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            <SelectItem value="engagement">Engagements</SelectItem>
            <SelectItem value="investment">Investments</SelectItem>
          </SelectContent>
        </Select>
        <Select value={reconcileFilter} onValueChange={v => { setReconcileFilter(v); setPage(1); }}>
          <SelectTrigger className="w-full lg:w-40">
            <SelectValue placeholder="Reconciled" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="true">Reconciled</SelectItem>
            <SelectItem value="false">Unreconciled</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant="outline" size="sm"
          onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          className="w-full lg:w-auto justify-center gap-1"
        >
          <Filter className="h-3.5 w-3.5" />
          {showAdvancedFilters ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </Button>
      </div>

      {/* Advanced Filters */}
      {showAdvancedFilters && (
        <Card className="border-border">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <label className="text-xs text-muted-foreground">From Date</label>
                <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="mt-1 w-40" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">To Date</label>
                <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="mt-1 w-40" />
              </div>
              <label className="flex items-center gap-2 text-sm text-[#242424]">
                <input type="checkbox" checked={hasNoc} onChange={e => setHasNoc(e.target.checked)} className="rounded border-gray-300" />
                Has NOC Code
              </label>
              <label className="flex items-center gap-2 text-sm text-[#242424]">
                <input type="checkbox" checked={hasReturn} onChange={e => setHasReturn(e.target.checked)} className="rounded border-gray-300" />
                Has Return Code
              </label>
              <Button variant="outline" size="sm" onClick={() => { setDateFrom(''); setDateTo(''); setHasNoc(false); setHasReturn(false); }}>
                Clear Filters
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Transaction Table */}
      <Card className="border-border">
        <CardContent className="p-0">
          <div className="md:hidden divide-y divide-border">
            {loading ? (
              <div className="p-10 text-center text-muted-foreground">
                <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full mx-auto" />
              </div>
            ) : transactions.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">No transactions found.</div>
            ) : transactions.map(txn => {
              const sc = statusConfig[txn.status] || statusConfig.pending;
              return (
                <button
                  key={txn.id}
                  onClick={() => { setSelected(txn); setShowDetail(true); }}
                  className="w-full p-4 text-left hover:bg-muted/40 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs font-mono text-[#242424] break-all">{txn.txn_id}</p>
                      <p className="mt-1 text-sm text-[#242424] truncate">{txn.engagement_title || txn.deal_name || '—'}</p>
                      <p className="text-xs text-muted-foreground break-all">{txn.user_email || '—'}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-semibold text-[#242424]">{formatCurrency(txn.amount_cents)}</p>
                      <p className="text-xs text-muted-foreground whitespace-nowrap">{formatDate(txn.created_at)}</p>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-1.5">
                    <Badge variant="outline" className="text-xs uppercase">{txn.method}</Badge>
                    <div className="flex items-center gap-1.5">
                      {sc.icon}
                      <Badge variant={sc.color} className="text-xs capitalize">{txn.status.replace('_', ' ')}</Badge>
                    </div>
                    {txn.reconciled && <Badge variant="outline" className="text-xs text-green-700 border-green-200">Reconciled</Badge>}
                    {txn.noc_code && <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-200">NOC:{txn.noc_code}</Badge>}
                    {txn.return_code && <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">{txn.return_code}</Badge>}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="hidden md:block overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="border-b border-border">
                <th className="p-3 w-10">
                  <input
                    type="checkbox"
                    checked={selectedIds.size === transactions.length && transactions.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300"
                  />
                </th>
                <th className="text-left p-3 text-xs text-muted-foreground font-medium">TXN ID</th>
                <th className="text-left p-3 text-xs text-muted-foreground font-medium">Source</th>
                <th className="text-left p-3 text-xs text-muted-foreground font-medium">Date</th>
                <th className="text-left p-3 text-xs text-muted-foreground font-medium">Engagement / Deal</th>
                <th className="text-left p-3 text-xs text-muted-foreground font-medium">User</th>
                <th className="text-right p-3 text-xs text-muted-foreground font-medium">Amount</th>
                <th className="text-left p-3 text-xs text-muted-foreground font-medium">Method</th>
                <th className="text-left p-3 text-xs text-muted-foreground font-medium">Bank Account</th>
                <th className="text-left p-3 text-xs text-muted-foreground font-medium">Status</th>
                <th className="text-left p-3 text-xs text-muted-foreground font-medium">Flags</th>
                <th className="text-left p-3 text-xs text-muted-foreground font-medium">Recon</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                    <td colSpan={13} className="p-12 text-center text-muted-foreground">
                    <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full mx-auto" />
                  </td>
                </tr>
              ) : transactions.length === 0 ? (
                <tr>
                  <td colSpan={13} className="p-12 text-center text-muted-foreground">
                    No transactions found.
                  </td>
                </tr>
              ) : transactions.map(txn => {
                const sc = statusConfig[txn.status] || statusConfig.pending;
                return (
                  <tr
                    key={txn.id}
                    className="border-b border-border hover:bg-muted/50 cursor-pointer"
                    onClick={() => { setSelected(txn); setShowDetail(true); }}
                  >
                    <td className="p-3" onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(txn.id)}
                        onChange={() => toggleSelected(txn.id)}
                        className="rounded border-gray-300"
                      />
                    </td>
                    <td className="p-3">
                      <span className="text-xs font-mono text-[#242424]">{txn.txn_id}</span>
                    </td>
                    <td className="p-3">
                      <Badge variant="outline" className={`text-[10px] uppercase ${
                        txn.transaction_type === 'vendor_payment' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                        txn.transaction_type === 'payroll' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                        txn.transaction_type === 'admin_send' ? 'bg-sky-50 text-sky-700 border-sky-200' :
                        txn.transaction_type === 'admin_debit' ? 'bg-rose-50 text-rose-700 border-rose-200' :
                        txn.source === 'investment' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                        'bg-purple-50 text-purple-700 border-purple-200'
                      }`}>
                        {txn.transaction_type === 'vendor_payment' ? 'VENDOR' :
                         txn.transaction_type === 'payroll' ? 'PAYROLL' :
                         txn.transaction_type === 'admin_send' ? 'SEND' :
                         txn.transaction_type === 'admin_debit' ? 'DEBIT' :
                         (txn.source || 'engagement').toUpperCase()}
                      </Badge>
                    </td>
                    <td className="p-3 text-sm text-muted-foreground whitespace-nowrap">
                      {formatDate(txn.created_at)}
                    </td>
                    <td className="p-3">
                      <p className="text-sm text-[#242424] truncate max-w-[160px]">{txn.engagement_title || txn.deal_name || '—'}</p>
                      <p className="text-xs text-muted-foreground font-mono">{txn.engagement_ref || ''}</p>
                    </td>
                    <td className="p-3 text-sm text-muted-foreground truncate max-w-[140px]">
                      {txn.user_email || '—'}
                    </td>
                    <td className="p-3 text-right">
                      <p className="text-sm font-medium text-[#242424]">{formatCurrency(txn.amount_cents)}</p>
                      {(txn.fees_cents || 0) > 0 && (
                        <p className="text-xs text-muted-foreground">-{formatCurrency(txn.fees_cents)} fee</p>
                      )}
                      {(txn.refund_amount_cents || 0) > 0 && (
                        <p className="text-xs text-orange-600">-{formatCurrency(txn.refund_amount_cents)} refund</p>
                      )}
                    </td>
                    <td className="p-3">
                      <Badge variant="outline" className="text-xs uppercase">{txn.method}</Badge>
                    </td>
                    <td className="p-3">
                      {txn.bank_account_name ? (
                        <div className="flex items-center gap-1.5">
                          <Landmark className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                          <span className="text-xs text-[#242424] truncate max-w-[120px]" title={txn.bank_account_name}>{txn.bank_account_name}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1.5">
                        {sc.icon}
                        <Badge variant={sc.color} className="text-xs capitalize">
                          {txn.status.replace('_', ' ')}
                        </Badge>
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex gap-1">
                        {txn.noc_code && (
                          <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-200">
                            NOC:{txn.noc_code}
                          </Badge>
                        )}
                        {txn.return_code && (
                          <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
                            {txn.return_code}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="p-3">
                      {txn.reconciled ? (
                        <Shield className="h-4 w-4 text-green-600" />
                      ) : txn.status === 'settled' ? (
                        <span className="text-xs text-amber-600">Pending</span>
                      ) : null}
                    </td>
                    <td className="p-3 text-right">
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-muted-foreground">
            Page {page} of {totalPages} ({total} total)
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}

      {/* ===== Transaction Detail Dialog ===== */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          {selected && (() => {
            const sc = statusConfig[selected.status] || statusConfig.pending;
            const maxRefundable = (selected.net_amount_cents || 0) - (selected.refund_amount_cents || 0);
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Receipt className="h-5 w-5" />
                    {selected.txn_id}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  {/* Amount & Status */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-2xl font-semibold text-[#242424]">{formatCurrency(selected.amount_cents)}</p>
                      {(selected.fees_cents || 0) > 0 && (
                        <p className="text-sm text-muted-foreground">Fees: {formatCurrency(selected.fees_cents)} | Net: {formatCurrency(selected.net_amount_cents)}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5">
                      {sc.icon}
                      <Badge variant={sc.color} className="capitalize text-sm">
                        {selected.status.replace('_', ' ')}
                      </Badge>
                    </div>
                  </div>

                  {/* Meta grid */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground">Source</p>
                      <Badge variant="outline" className={`text-xs uppercase mt-1 ${
                        selected.transaction_type === 'vendor_payment' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                        selected.transaction_type === 'payroll' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                        selected.transaction_type === 'admin_send' ? 'bg-sky-50 text-sky-700 border-sky-200' :
                        selected.transaction_type === 'admin_debit' ? 'bg-rose-50 text-rose-700 border-rose-200' :
                        selected.source === 'investment' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                        'bg-purple-50 text-purple-700 border-purple-200'
                      }`}>
                        {selected.transaction_type === 'vendor_payment' ? 'VENDOR' :
                         selected.transaction_type === 'payroll' ? 'PAYROLL' :
                         selected.transaction_type === 'admin_send' ? 'SEND' :
                         selected.transaction_type === 'admin_debit' ? 'DEBIT' :
                         (selected.source || 'engagement').toUpperCase()}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">{selected.source === 'investment' ? 'Deal' : 'Engagement'}</p>
                      <p className="text-[#242424]">{selected.engagement_title || selected.deal_name || '—'}</p>
                      <p className="text-xs font-mono text-muted-foreground">{selected.engagement_ref || ''}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">User</p>
                      <p className="text-[#242424]">{selected.user_email || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Method</p>
                      <p className="text-[#242424] uppercase">{selected.method}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Bank Account</p>
                      {selected.bank_account_name ? (
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <Landmark className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                          <div>
                            <p className="text-[#242424]">{selected.bank_account_name}</p>
                            {selected.bank_account_increase_id && (
                              <p className="text-[10px] font-mono text-muted-foreground">{selected.bank_account_increase_id}</p>
                            )}
                          </div>
                        </div>
                      ) : (
                        <p className="text-muted-foreground">—</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Currency</p>
                      <p className="text-[#242424]">{selected.currency}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Created</p>
                      <p className="text-[#242424]">{formatDateTime(selected.created_at)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Settled</p>
                      <p className="text-[#242424]">{formatDateTime(selected.settled_at)}</p>
                    </div>
                  </div>

                  {/* ACH Details */}
                  {selected.method === 'ach' && (
                    <Card className="border-border bg-gray-50">
                      <CardContent className="p-4 space-y-2">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">ACH Details</p>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          {selected.ach_account_last4 && (
                            <div>
                              <p className="text-xs text-muted-foreground">Account</p>
                              <p className="text-[#242424]">****{selected.ach_account_last4}</p>
                            </div>
                          )}
                          {selected.ach_transfer_id && (
                            <div>
                              <p className="text-xs text-muted-foreground">Transfer ID</p>
                              <p className="text-[#242424] font-mono text-xs">{selected.ach_transfer_id}</p>
                            </div>
                          )}
                          {selected.batch_id && (
                            <div>
                              <p className="text-xs text-muted-foreground">Batch</p>
                              <p className="text-[#242424] font-mono text-xs">{selected.batch_id}</p>
                            </div>
                          )}
                          {selected.settlement_date && (
                            <div>
                              <p className="text-xs text-muted-foreground">Settlement Date</p>
                              <p className="text-[#242424]">{formatDate(selected.settlement_date)}</p>
                            </div>
                          )}
                          {selected.expected_settlement_date && (
                            <div>
                              <p className="text-xs text-muted-foreground">Funds Available</p>
                              <p className={selected.settlement_date ? 'text-[#242424]' : 'text-amber-600 font-medium'}>{formatDate(selected.expected_settlement_date)}</p>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* USDC Details */}
                  {selected.method === 'usdc' && (selected.wallet_address || selected.tx_hash) && (
                    <Card className="border-border bg-gray-50">
                      <CardContent className="p-4 space-y-2">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">USDC / On-Chain</p>
                        <div className="text-sm space-y-1">
                          {selected.chain && <p className="text-xs text-muted-foreground">Chain: <span className="text-[#242424] capitalize">{selected.chain}</span></p>}
                          {selected.wallet_address && <p className="text-xs font-mono text-[#242424] break-all">{selected.wallet_address}</p>}
                          {selected.tx_hash && <p className="text-xs font-mono text-blue-600 break-all">{selected.tx_hash}</p>}
                          {selected.confirmations > 0 && <p className="text-xs text-muted-foreground">{selected.confirmations} confirmations</p>}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* NOC Code */}
                  {selected.noc_code && (
                    <Card className="border-orange-200 bg-orange-50">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="h-4 w-4 text-orange-600 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-orange-800">NOC: {selected.noc_code}</p>
                            <p className="text-xs text-orange-700">{NOC_CODES[selected.noc_code] || 'Notification of Change'}</p>
                            {selected.noc_corrected && (
                              <pre className="text-xs mt-1 bg-white/60 rounded p-2 text-orange-900">
                                {JSON.stringify(selected.noc_corrected, null, 2)}
                              </pre>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Return Code */}
                  {selected.return_code && (
                    <Card className="border-red-200 bg-red-50">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-2">
                          <XCircle className="h-4 w-4 text-red-600 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-red-800">Return: {selected.return_code}</p>
                            <p className="text-xs text-red-700">{RETURN_CODES[selected.return_code] || selected.return_reason || 'ACH Return'}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Refund Info */}
                  {(selected.refund_amount_cents || 0) > 0 && (
                    <Card className="border-border bg-gray-50">
                      <CardContent className="p-4">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Refund History</p>
                        <div className="grid grid-cols-2 gap-2 text-sm mt-2">
                          <div>
                            <p className="text-xs text-muted-foreground">Refunded</p>
                            <p className="text-orange-700 font-medium">{formatCurrency(selected.refund_amount_cents)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Remaining</p>
                            <p className="text-[#242424]">{formatCurrency(maxRefundable)}</p>
                          </div>
                          {selected.refund_reason && (
                            <div className="col-span-2">
                              <p className="text-xs text-muted-foreground">Reason</p>
                              <p className="text-[#242424]">{selected.refund_reason}</p>
                            </div>
                          )}
                          {selected.refund_ref && (
                            <div className="col-span-2">
                              <p className="text-xs text-muted-foreground">Reference</p>
                              <p className="text-[#242424] font-mono text-xs">{selected.refund_ref}</p>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Reconciliation */}
                  <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <Shield className={`h-4 w-4 ${selected.reconciled ? 'text-green-600' : 'text-gray-400'}`} />
                      <span className="text-sm text-[#242424]">
                        {selected.reconciled ? `Reconciled ${formatDateTime(selected.reconciled_at)}` : 'Not reconciled'}
                      </span>
                    </div>
                    {!selected.reconciled && selected.status === 'settled' && (
                      <Button variant="outline" size="sm" onClick={() => handleReconcile(selected)} disabled={actionLoading}>
                        Mark Reconciled
                      </Button>
                    )}
                  </div>
                </div>

                <DialogFooter className="flex-col sm:flex-row gap-2">
                  {selected.status === 'pending' && (
                    <Button
                      variant="outline" size="sm"
                      onClick={() => handleCancel(selected)}
                      disabled={actionLoading}
                      className="gap-1 text-red-600 hover:text-red-700"
                    >
                      <Ban className="h-4 w-4" /> Cancel Transaction
                    </Button>
                  )}
                  {['settled', 'processing'].includes(selected.status) && maxRefundable > 0 && (
                    <Button
                      variant="outline" size="sm"
                      onClick={() => { setRefundAmount(''); setRefundReason(''); setShowRefund(true); }}
                      className="gap-1 text-orange-600 hover:text-orange-700"
                    >
                      <Undo2 className="h-4 w-4" /> Refund
                    </Button>
                  )}
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ===== Refund Dialog ===== */}
      <Dialog open={showRefund} onOpenChange={setShowRefund}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Issue Refund</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 py-2">
              <p className="text-sm text-muted-foreground">
                Transaction <span className="font-mono">{selected.txn_id}</span> —
                Max refundable: <span className="font-medium text-[#242424]">
                  {formatCurrency((selected.net_amount_cents || 0) - (selected.refund_amount_cents || 0))}
                </span>
              </p>
              <div>
                <label className="text-sm font-medium text-[#242424]">Refund Amount (USD)</label>
                <Input
                  type="number"
                  value={refundAmount}
                  onChange={e => setRefundAmount(e.target.value)}
                  placeholder="Leave empty for full refund"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#242424]">Reason</label>
                <Input
                  value={refundReason}
                  onChange={e => setRefundReason(e.target.value)}
                  placeholder="Optional reason for the refund"
                  className="mt-1"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRefund(false)}>Cancel</Button>
            <Button onClick={handleRefund} disabled={actionLoading} className="gap-1 bg-orange-600 hover:bg-orange-700">
              {actionLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Undo2 className="h-4 w-4" />}
              {refundAmount ? `Refund $${refundAmount}` : 'Full Refund'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== New Transfer Dialog ===== */}
      <Dialog open={showTransfer} onOpenChange={(o) => { if (!o) setShowTransfer(false); }}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" /> New Transfer
            </DialogTitle>
          </DialogHeader>

          {transferResult ? (
            <div className="py-6 text-center space-y-3">
              <CheckCircle className="h-10 w-10 text-green-600 mx-auto" />
              <p className="text-sm text-green-800 font-medium">{transferResult}</p>
              <Button variant="outline" size="sm" onClick={() => { resetTransferForm(); setShowTransfer(false); }}>
                Close
              </Button>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              {/* Direction toggle */}
              <div>
                <label className="text-sm font-medium text-[#242424] mb-1.5 block">Direction</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setTransferDirection('send')}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${
                      transferDirection === 'send' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}
                  >
                    <ArrowUp className="h-4 w-4" /> Send (Credit)
                  </button>
                  <button
                    onClick={() => { setTransferDirection('debit'); setTransferMethod('ach'); }}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${
                      transferDirection === 'debit' ? 'border-red-500 bg-red-50 text-red-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}
                  >
                    <ArrowDown className="h-4 w-4" /> Debit (Pull)
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {transferDirection === 'send'
                    ? 'Send money via ACH credit or record a USDC send'
                    : 'Pull money from a bank account via ACH debit'}
                </p>
              </div>

              {/* Method (only for send) */}
              {transferDirection === 'send' && (
                <div>
                  <label className="text-sm font-medium text-[#242424] mb-1.5 block">Method</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setTransferMethod('ach')}
                      className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                        transferMethod === 'ach' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'
                      }`}
                    >
                      <CreditCard className="h-4 w-4" /> ACH
                    </button>
                    <button
                      onClick={() => setTransferMethod('usdc')}
                      className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                        transferMethod === 'usdc' ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'
                      }`}
                    >
                      <Wallet className="h-4 w-4" /> USDC
                    </button>
                  </div>
                </div>
              )}

              {/* Recipient / Payer info */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-[#242424]">
                    {transferDirection === 'send' ? 'Recipient Name' : 'Payer Name'} *
                  </label>
                  <Input value={transferName} onChange={e => setTransferName(e.target.value)}
                    placeholder="John Doe" className="mt-1" />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#242424]">Email</label>
                  <Input value={transferEmail} onChange={e => setTransferEmail(e.target.value)}
                    placeholder="john@example.com" className="mt-1" />
                </div>
              </div>

              {/* Amount */}
              <div>
                <label className="text-sm font-medium text-[#242424]">Amount (USD) *</label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                  <Input type="number" value={transferAmount} onChange={e => setTransferAmount(e.target.value)}
                    placeholder="0.00" step="0.01" min="0.01" className="pl-7" />
                </div>
              </div>

              {/* ACH Details */}
              {(transferDirection === 'debit' || transferMethod === 'ach') && (
                <div className="space-y-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Bank Account Details</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-[#242424]">Routing Number *</label>
                      <Input value={transferRouting}
                        onChange={e => setTransferRouting(e.target.value.replace(/\D/g, '').slice(0, 9))}
                        placeholder="021000021" maxLength={9} className="mt-1 font-mono" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-[#242424]">Account Number *</label>
                      <Input type="password" value={transferAccount}
                        onChange={e => setTransferAccount(e.target.value.replace(/\D/g, '').slice(0, 17))}
                        placeholder="Account number" maxLength={17} className="mt-1 font-mono" autoComplete="off" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#242424]">Account Type</label>
                    <div className="flex gap-2 mt-1">
                      {(['checking', 'savings'] as const).map(t => (
                        <button key={t} onClick={() => setTransferAccountType(t)}
                          className={`flex-1 py-1.5 rounded-md border text-xs font-medium capitalize transition-all ${
                            transferAccountType === t ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'
                          }`}>{t}</button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* USDC Details */}
              {transferDirection === 'send' && transferMethod === 'usdc' && (
                <div className="space-y-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <p className="text-xs font-semibold text-purple-700 uppercase tracking-wide">USDC Details</p>
                  <div>
                    <label className="text-xs font-medium text-[#242424]">Wallet Address *</label>
                    <Input value={transferWallet} onChange={e => setTransferWallet(e.target.value.trim())}
                      placeholder="0x742d35Cc6634C0532925a3b844Bc9e..." className="mt-1 font-mono" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#242424]">Chain</label>
                    <div className="grid grid-cols-4 gap-1.5 mt-1">
                      {(['base', 'ethereum', 'polygon', 'arbitrum'] as const).map(c => (
                        <button key={c} onClick={() => setTransferChain(c)}
                          className={`py-1.5 rounded-md border text-xs font-medium capitalize transition-all ${
                            transferChain === c ? 'border-purple-500 bg-white text-purple-700' : 'border-purple-100 text-purple-500 hover:border-purple-300'
                          }`}>{c}</button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Description & Notes */}
              <div>
                <label className="text-sm font-medium text-[#242424]">Description</label>
                <Input value={transferDesc} onChange={e => setTransferDesc(e.target.value)}
                  placeholder="Payment for services" className="mt-1" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#242424]">Notes</label>
                <Input value={transferNotes} onChange={e => setTransferNotes(e.target.value)}
                  placeholder="Internal notes (not visible to recipient)" className="mt-1" />
              </div>

              {transferError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-700">{transferError}</p>
                </div>
              )}

              {/* ACH Same-Day Cutoff Disclaimer */}
              {(transferDirection === 'debit' || transferMethod === 'ach') && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                  <Clock className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-800 leading-relaxed">
                    <strong>Same-Day ACH Notice:</strong> ACH transactions initiated before <strong>4:45 PM ET</strong> on a business day are eligible for same-day processing. Transactions submitted after this cutoff or on weekends/holidays will be processed on the next business day. All ACH transactions are subject to <strong>NACHA Operating Rules</strong>.
                  </p>
                </div>
              )}

              {/* Warning banner */}
              <div className={`rounded-lg p-3 border flex items-start gap-2 ${
                transferDirection === 'debit'
                  ? 'bg-red-50 border-red-200'
                  : 'bg-blue-50 border-blue-200'
              }`}>
                <AlertTriangle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${
                  transferDirection === 'debit' ? 'text-red-600' : 'text-blue-600'
                }`} />
                <p className={`text-xs ${transferDirection === 'debit' ? 'text-red-700' : 'text-blue-700'}`}>
                  {transferDirection === 'debit'
                    ? 'This will immediately initiate an ACH debit, pulling money from the specified bank account. This action cannot be easily reversed.'
                    : transferMethod === 'ach'
                      ? 'This will initiate an ACH credit, sending money from your Increase account. Funds will be debited from your account.'
                      : 'This records the USDC send for tracking. You must manually send the USDC on-chain to the specified wallet.'}
                </p>
              </div>
            </div>
          )}

          {!transferResult && (
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTransfer(false)}>Cancel</Button>
              <Button
                onClick={handleTransferSubmit}
                disabled={actionLoading || !transferName || !transferAmount ||
                  ((transferDirection === 'debit' || transferMethod === 'ach') && (transferRouting.length !== 9 || transferAccount.length < 4)) ||
                  (transferDirection === 'send' && transferMethod === 'usdc' && transferWallet.length < 10)}
                className={`gap-1 ${
                  transferDirection === 'debit'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-blue-600 hover:bg-blue-700'
                } text-white`}
              >
                {actionLoading ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : transferDirection === 'send' ? (
                  <ArrowUp className="h-4 w-4" />
                ) : (
                  <ArrowDown className="h-4 w-4" />
                )}
                {transferDirection === 'send' ? 'Send' : 'Debit'} {transferAmount ? `$${transferAmount}` : ''}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTransactions;
